import { useState } from 'react';
import type { NativeSignIn } from '../native/bridge.js';
import { useT } from '../i18n/LocaleProvider.js';

/**
 * 네이티브 셸의 로그인 버튼 (v17 R4 · `docs/40` §5).
 *
 * ── GIS 대신 이것을 그리는 이유 ──────────────────────────────
 *
 * WKWebView 안의 GIS 는 구글이 막는다(임베디드 user-agent). 셸은 그 대신 시스템
 * 브라우저를 띄우므로 **버튼은 우리가 그리고 시트는 셸이 띄운다.**
 *
 * **`getNativeBridge()` 를 여기서 다시 읽지 않는다.** 판정은 `loginMode` 가 이미 했고,
 * DOM 을 읽는 줄은 저장소에 `bridge.ts` 하나뿐이다.
 *
 * ── 상태가 셋인 이유 ────────────────────────────────────────
 *
 * `GoogleButton` 이 같은 자리에서 배운 것이다 — **이 경로의 실패는 전부 「아무 일도 안
 * 일어남」으로만 보인다.** 그래서 말해야 할 것과 말하지 말아야 할 것을 나눈다.
 *
 * - **취소는 아무 말도 하지 않는다.** 시청자가 스스로 시트를 닫은 것이고, 거기에
 *   빨간 문장을 띄우면 자기가 한 일을 고장으로 읽는다. iOS 는 첫 로그인에 시스템
 *   경고(「"BLOOM"이(가) "google.com"에 로그인하려고 합니다」)를 띄우고 거기서 [취소] 를
 *   누르는 것도 이 갈래다
 * - **실패는 말한다.** 다시 눌러 볼 값이 있다
 *
 * ── 성공하면 화면이 사라진다 ────────────────────────────────
 *
 * `loginWithGoogle` 이 성공하면 `window.location.replace` 로 페이지를 다시 연다.
 * 그래서 `busy` 를 풀 일이 없지만 **`finally` 로 푼다** — 로그인 요청이 실패하면
 * 페이지가 그대로 남고, 그때 버튼이 「로그인 중…」에 멈춰 있으면 다시 누를 수 없다.
 */
export function NativeSignInButton({
  signIn,
  onIdToken,
}: {
  signIn: () => Promise<NativeSignIn>;
  onIdToken: (idToken: string) => Promise<void> | void;
}) {
  const t = useT();
  const [state, setState] = useState<'idle' | 'busy' | 'failed'>('idle');

  const run = async (): Promise<void> => {
    setState('busy');
    try {
      const result = await signIn();
      if (result.status === 'cancelled') {
        setState('idle');
        return;
      }
      if (result.status === 'failed') {
        setState('failed');
        return;
      }
      await onIdToken(result.idToken);
    } finally {
      setState((prev) => (prev === 'busy' ? 'idle' : prev));
    }
  };

  return (
    <div>
      {/* 네이티브 `<button>` 이다 — 리모컨 포커스 링이 그대로 잡는다 (절대 규칙 29·30).
          교차 출처 iframe 인 GIS 버튼에 닿지 않던 한계가 **셸에서만** 갚아진다 */}
      <button
        type="button"
        className="button button--secondary"
        disabled={state === 'busy'}
        onClick={() => void run()}
      >
        {state === 'busy' ? t('login.nativeSigningIn') : t('login.nativeButton')}
      </button>
      {state === 'failed' && <p className="card__text">{t('login.nativeFailed')}</p>}
    </div>
  );
}
